import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { css } from '@linaria/core';
import { IconButton } from '@cbhq/cds-web/buttons';
import { HStack } from '@cbhq/cds-web/layout/HStack';
import { VStack } from '@cbhq/cds-web/layout/VStack';
import { TextTitle3 } from '@cbhq/cds-web/typography/TextTitle3';

const scrollBarStyles = css`
  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */

  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
`;

interface SimpleCarouselProps {
  items: ReactNode[];
  title?: string;
}

function SimpleCarousel({ items, title }: SimpleCarouselProps) {
  const scrollableRef = useRef<HTMLElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollability = useCallback(() => {
    if (scrollableRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollableRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  }, []);

  useEffect(() => {
    updateScrollability();
    window.addEventListener('resize', updateScrollability);
    return () => {
      window.removeEventListener('resize', updateScrollability);
    };
  }, [updateScrollability]);

  const scrollHandler = useCallback(
    (direction: 'left' | 'right') => () => {
      if (scrollableRef.current) {
        const { clientWidth } = scrollableRef.current;
        const distance = clientWidth * (direction === 'left' ? -1 : 1);
        scrollableRef.current.scrollBy({
          left: distance,
          behavior: 'smooth',
        });
      }
    },
    [],
  );

  return (
    <VStack spacing={7} gap={2}>
      <HStack justifyContent="space-between" alignItems="center">
        <TextTitle3 as="h2">{title}</TextTitle3>
        <HStack gap={1}>
          <IconButton
            name="backArrow"
            transparent
            onPress={scrollHandler('left')}
            disabled={!canScrollLeft}
          />
          <IconButton
            name="forwardArrow"
            transparent
            onPress={scrollHandler('right')}
            disabled={!canScrollRight}
          />
        </HStack>
      </HStack>
      <HStack
        className={scrollBarStyles}
        overflow="auto"
        key={items.length}
        ref={scrollableRef}
        onScroll={updateScrollability}
        gap={2}
        position="relative"
      >
        {items}
      </HStack>
    </VStack>
  );
}

export default SimpleCarousel;
